/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import useLocateService from '@rakuten/hooks/useLocateService';
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import useErrorState from '@rakuten/hooks/useErrorState';
import { redirectExternally } from '@rakuten/common-util/location';
import { View } from '@rakuten/modules-core-components';
import Label from '@rakuten/components/atoms/Label';
import Button from '@rakuten/components/molecules/Button';
import Dialog from '@rakuten/components/molecules/Dialog';
import ErrorDialog from '@rakuten/components/molecules/ErrorDialog';
import ActivityIndicator from '@rakuten/components/molecules/ActivityIndicator';
import usePushToCloud from './hooks/usePushToCloud';


const EPub = React.lazy(() =>
  import('@rakuten/modules-rx-tolino-epub')
);


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

function createStyles() {
  return {
    container: {
      flex: '0 0 100%',
      height: '100%',
      width: '100%'
    }
  };
}


// -----------------------------------------------------------------------------
// APP COMPONENT
// -----------------------------------------------------------------------------

function App() {
  // Initialize state variables
  const [ errorState, setErrorState ] = useErrorState();
  const [ buffer, setBuffer ] = useState(null);
  const [ fetchService, queryString, securityService ] = useLocateService([ 'fetch', 'queryString', 'security' ]);
  const [ pushToCloudState, pushToCloudStates, resetPushToCloudState ] = usePushToCloud();
  const { t } = useTranslation();

  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'App',
      styleFn: createStyles
    }
  );


  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------

  useEffect(function componentDidMount() {
    const url = queryString?.getQueryParameter('epub_url');
    if (url) {
      securityService?.validateURLAgainstWhiteList(url).then(
        () => {
          fetchService?.fetchAsArrayBuffer({ url }).then(
            setBuffer
          ).catch(
            reason => setErrorState(reason)
          );
        }
      ).catch(
        reason => setErrorState(reason)
      );
    }
  }, [ fetchService, queryString, securityService ]);


  // ---------------------------------------------------------------------------
  // ERROR HANDLER
  // ---------------------------------------------------------------------------

  function handleErrorButtonPress(/* e */) {
    const backrefURL = queryString.getQueryParameter('backref_url');
    if (backrefURL) {
      redirectExternally(backrefURL);
    } else {
      window.history.back(-1);
    }
  }


  function handleP2CButtonPress(/* e */) {
    resetPushToCloudState();
  }


  // ---------------------------------------------------------------------------
  // PUSH TO CLOUD
  // ---------------------------------------------------------------------------

  // The user requested to push a preview publication into their shelf
  if (pushToCloudState === pushToCloudStates.PUSHING) {
    return <ActivityIndicator />;
  }

  // Pushing a preview publication to a user's shelf was successful
  if (pushToCloudState === pushToCloudStates.SUCCESS) {
    return (
      <Dialog
        showCloseIcon={ false }
        primaryActionButton={
          <Button
            text={ t('OK') }
            onPress={ handleP2CButtonPress }
          />
        }
      >
        <Label
          text={ t('The reading sample has been successfully added to your library.') }
          numberOfLines={ 0 }
        />
      </Dialog>
    );
  }

  // Pushing a preview publication to a user's shelf was unsuccessful
  // TODO: Do not handle P2C errors like this, instead use our error handling
  //       system, i.e. throw a P2CError and handle the error further down in
  //       the render section.
  if (pushToCloudState === pushToCloudStates.FAIL) {
    return (
      <ErrorDialog
        showCloseIcon={ false }
        primaryActionButton={
          <Button
            text={ t('OK') }
            onPress={ handleP2CButtonPress }
          />
        }
      >
        <Label
          text={ t('An error occurred while saving the reading sample. Please try again later.', { ns: 'error' }) }
          numberOfLines={ 0 }
        />
      </ErrorDialog>
    );
  }


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  if (errorState.hasError === true) {
    let title = '';
    if (securityService && errorState.error instanceof securityService.SecurityServiceError) {
      title = 'Security error';
    } else if (fetchService && errorState.error instanceof fetchService.FetchServiceError) {
      title = 'Network error';
    }

    return (
      <ErrorDialog
        title={ t(title, { ns: 'error' }) }
        details={ errorState.error }
        primaryActionButton={
          <Button
            text={ t('Back') }
            onPress={ handleErrorButtonPress }
          />
        }
      >
        <Label
          text={ t(errorState.error.message, { ns: 'error' }) }
          numberOfLines={ 0 }
        />
      </ErrorDialog>
    );
  }

  return !buffer
    ? (
      <ActivityIndicator />
    )
    : (
      <View style={ styles.container }>
        <EPub
          backRefURL={ queryString.getQueryParameter('backref_url') }
          buffer={ buffer }
          endOfBook={ queryString.getQueryParameter('end_of_book') }
          engine={ queryString.getQueryParameter('engine') || 'ur' }
          publicationId={ queryString.getQueryParameter('publication_id') }
          purchaseURL={ queryString.getQueryParameter('purchase_url') }
          purchaseURLs={ queryString.getQueryParameter('purchase_urls', true) }
          resellerId={ queryString.getQueryParameter('reseller_id') }
        />
      </View>
    );
}


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default App;
